import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useProjects } from "../lib/ProjectContext";
import { api } from "../lib/apiClient";
import { useCurrentAppUser } from "../lib/useCurrentAppUser";
import { DocumentsTable } from "../components/DocumentsTable";
import { DraftTableReviewModal } from "../components/DraftTableReviewModal";
import type { DocumentRecord } from "@shared/types";

export function ContractDocsPage() {
  const { selectedProjectId, projects } = useProjects();
  const { role } = useCurrentAppUser();
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reviewDoc, setReviewDoc] = useState<DocumentRecord | null>(null);
  const project = projects.find((p) => p.id === selectedProjectId);

  function load() {
    if (!selectedProjectId) return;
    setLoading(true);
    api
      .get<{ documents: DocumentRecord[] }>(`/api/projects/${selectedProjectId}/sources`)
      .then((data) => setDocuments(data.documents.filter((d) => d.projectId !== null)))
      .catch((err) => setError(err.message || "Failed to load project documents."))
      .finally(() => setLoading(false));
  }

  useEffect(load, [selectedProjectId]);

  const health = useMemo(() => {
    const active = documents.filter((d) => d.status !== "archived");
    return {
      total: active.length,
      approved: active.filter((d) => d.status === "approved").length,
      drafts: active.filter((d) => d.status === "draft").length,
      ready: active.filter((d) => d.ingestStatus === "ready").length,
      processing: active.filter((d) => d.ingestStatus === "processing" || d.ingestStatus === "queued").length,
      failed: active.filter((d) => d.ingestStatus === "failed").length,
      plans: active.filter((d) => d.sourceType === "contract_plans").length,
    };
  }, [documents]);

  async function reextract(doc: DocumentRecord) {
    if (!selectedProjectId) return;
    // On an approved document this endpoint does not repair anything in place:
    // it creates a new draft replacement and re-runs the full Azure extraction.
    // Spell that out, and say so twice when the reviewed corrections have
    // already been re-applied, since there is then usually nothing to gain.
    if (doc.status === "approved") {
      const alreadyRepaired = (doc.reviewedExtractionRevision ?? 0) >= 1;
      const warning = [
        `Replace "${doc.title}" with a fresh Azure extraction?`,
        "",
        "This does not edit the current version. It creates a new draft replacement and re-runs extraction over every page, which takes a while. The current version stays live until you approve the replacement.",
        alreadyRepaired
          ? "\nNote: this document's reviewed table corrections have already been re-applied (revision 1). A replacement starts over at revision 0 and has to be repaired again before it can be approved."
          : "",
      ].join("\n");
      if (!window.confirm(warning)) return;
    }
    setBusyId(doc.id);
    setError(null);
    try {
      await api.post(`/api/projects/${selectedProjectId}/documents/${doc.id}/reextract`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reprocessing failed.");
    } finally { setBusyId(null); }
  }

  async function approve(doc: DocumentRecord) {
    if (!selectedProjectId) return;
    setBusyId(doc.id);
    try {
      await api.post(`/api/projects/${selectedProjectId}/documents/${doc.id}/approve`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Approval failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function archive(doc: DocumentRecord) {
    if (!selectedProjectId) return;
    setBusyId(doc.id);
    try {
      await api.post(`/api/projects/${selectedProjectId}/documents/${doc.id}/archive`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Archive failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function unarchive(doc: DocumentRecord) {
    if (!selectedProjectId) return;
    setBusyId(doc.id);
    try {
      await api.post(`/api/projects/${selectedProjectId}/documents/${doc.id}/unarchive`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unarchive failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function del(doc: DocumentRecord) {
    if (!selectedProjectId) return;
    setBusyId(doc.id);
    try {
      await api.post(`/api/projects/${selectedProjectId}/documents/${doc.id}/delete`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed.");
    } finally {
      setBusyId(null);
    }
  }

  if (loading) {
    return (
      <div className="cei-empty-state">
        <img src="/ceinexus-mark.svg" alt="" />
        <div>
          <strong>Preparing project intelligence</strong>
          <span>Loading plans, provisions, directives, and processing status…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="content-col-wide project-docs-workspace">
      <section className="workspace-hero">
        <div>
          <span className="workspace-eyebrow">PROJECT INTELLIGENCE</span>
          <h1>Project Documents</h1>
          <p>
            {project?.name ? `${project.name} · ` : ""}The controlling plans, special provisions, directives, and project records used by CEI Nexus Assist.
          </p>
        </div>
        {role === "owner" && (
          <Link className="btn btn-primary workspace-primary-action" to="/upload">
            <span aria-hidden="true">＋</span> Add documents
          </Link>
        )}
      </section>

      <div className="document-health-grid" aria-label="Project document health">
        <div className="document-health-card"><span>Approved knowledge</span><strong>{health.approved}</strong><small>of {health.total} active documents</small></div>
        <div className="document-health-card"><span>Plan sets</span><strong>{health.plans}</strong><small>see interpretation coverage below</small></div>
        <div className="document-health-card"><span>Processing</span><strong>{health.processing}</strong><small>{health.processing ? "document intelligence running" : "pipeline clear"}</small></div>
        <div className={`document-health-card ${health.failed ? "health-alert" : ""}`}><span>Processing / approval issues</span><strong>{health.failed + health.drafts}</strong><small>{health.failed ? `${health.failed} processing failure${health.failed === 1 ? "" : "s"}` : health.drafts ? `${health.drafts} draft${health.drafts === 1 ? "" : "s"} awaiting review` : "see page coverage for interpretation gaps"}</small></div>
      </div>

      <div className="workspace-context-bar">
        <div><span className="context-dot context-dot-live" /> <strong>{health.ready}</strong> searchable now</div>
        <div><span className="context-dot" /> Current project only</div>
        <Link to="/sources">Open shared knowledge library →</Link>
      </div>

      {error && <div className="workspace-alert">{error}</div>}
      <section className="document-library-panel">
        <div className="panel-title-row">
          <div>
            <span className="section-kicker">DOCUMENT REGISTER</span>
            <h2>Contract intelligence</h2>
          </div>
          <span className="muted">{health.total} active</span>
        </div>
        <DocumentsTable
          documents={documents}
          role={role}
          busyId={busyId}
          onReextract={reextract}
          onApprove={approve}
          onArchive={archive}
          onUnarchive={unarchive}
          onDelete={del}
          onReviewTables={setReviewDoc}
          emptyMessage="No project documents have been added yet."
        />
      </section>
      {reviewDoc && selectedProjectId && (
        <DraftTableReviewModal
          projectId={selectedProjectId}
          document={reviewDoc}
          onClose={() => setReviewDoc(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

